import React,{ useEffect,useState } from 'react';
import { useNavigate,useParams } from 'react-router-dom';
import ActionEditor from './ActionEditor';
import './AutomationEditor.css';
import ConditionEditor from './ConditionEditor';
import { useAuth } from '../../context/AuthContext';

const AutomationEditor = () => {
  const { automationId } = useParams();
  const navigate = useNavigate();
  const { apiRequest } = useAuth();
  
  
  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [scenes, setScenes] = useState([]);
  
  // For editing basic info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  
  // Flags for editing mode
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [isAddingAction, setIsAddingAction] = useState(false);
  const [editingConditionIndex, setEditingConditionIndex] = useState(null);
  const [editingActionIndex, setEditingActionIndex] = useState(null);

const API_BASE_URL = '';
const API_PATH = '/api';
  
  // Load automation data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load automation details
        const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}`);
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const automationData = data;
        
        setAutomation(automationData);
        setName(automationData.name);
        setDescription(automationData.description || '');
        setActive(automationData.active);
        
        // Load devices and scenes for the editors
        const [devicesResult, scenesResult] = await Promise.all([
          apiRequest(`${API_BASE_URL}${API_PATH}/devices`),
          apiRequest(`${API_BASE_URL}${API_PATH}/scenes`)
        ]);
        
        const { response: devicesResponse, data: devicesData } = devicesResult;
        const { response: scenesResponse, data: scenesData } = scenesResult;
        
        if (!devicesResponse.ok || !scenesResponse.ok) {
          throw new Error('Failed to load devices or scenes');
        }
        
        // const devicesData = await devicesResponse.json();
        // const scenesData = await scenesResponse.json();
        
        setDevices(devicesData);
        setScenes(scenesData);
        
        setError(null);
      } catch (err) {
        console.error('Error loading automation data:', err);
        setError('Failed to load automation data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [automationId,apiRequest]);

  // Save basic automation info
  const handleSaveBasicInfo = async () => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          description,
          active
        }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Update local state
      setAutomation(prev => ({
        ...prev,
        name,
        description,
        active
      }));
    } catch (err) {
      console.error('Error saving automation:', err);
      setError('Failed to save automation');
    }
  };

  // Add a new condition
  const handleAddCondition = async (condition) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/add-condition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ condition }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        conditions: [...(prev.conditions || []), condition]
      }));
      
      // Exit adding mode
      setIsAddingCondition(false);
    } catch (err) {
      console.error('Error adding condition:', err);
      setError('Failed to add condition');
    }
  };

  // Update an existing condition
  const handleUpdateCondition = async (condition, index) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/conditions/${index}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ condition }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        conditions: prev.conditions.map((c, i) => i === index ? condition : c)
      }));
      
      // Exit editing mode
      setEditingConditionIndex(null);
    } catch (err) {
      console.error('Error updating condition:', err);
      setError('Failed to update condition');
    }
  };

  // Delete a condition
  const handleDeleteCondition = async (index) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/conditions/${index}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        conditions: prev.conditions.filter((_, i) => i !== index)
      }));
    } catch (err) {
      console.error('Error deleting condition:', err);
      setError('Failed to delete condition');
    }
  };

  // Add a new action
  const handleAddAction = async (action) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/add-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        actions: [...(prev.actions || []), action]
      }));
      
      // Exit adding mode
      setIsAddingAction(false);
    } catch (err) {
      console.error('Error adding action:', err);
      setError('Failed to add action');
    }
  };

  // Update an existing action
  const handleUpdateAction = async (action, index) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/actions/${index}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action }),
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        actions: prev.actions.map((a, i) => i === index ? action : a)
      }));
      
      // Exit editing mode
      setEditingActionIndex(null);
    } catch (err) {
      console.error('Error updating action:', err);
      setError('Failed to update action');
    }
  };

  // Delete an action
  const handleDeleteAction = async (index) => {
    try {
      const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/automations/${automationId}/actions/${index}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      // Optimistically update local state instead of refetching
      setAutomation(prev => ({
        ...prev,
        actions: prev.actions.filter((_, i) => i !== index)
      }));
    } catch (err) {
      console.error('Error deleting action:', err);
      setError('Failed to delete action');
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return <div className="loading">Loading automation...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!automation) {
    return <div className="error">Automation not found</div>;
  }

  return (
    <div className="automation-editor">
      <div className="automation-editor-header">
        <button 
          className="back-button"
          onClick={() => navigate('/automations')}
        >
          &larr; Back to Automations
        </button>
        
        <h2>Edit Automation</h2>
      </div>
      
      <div className="automation-basic-info">
        <div className="form-group">
          <label htmlFor="name">Name</label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="active" className="checkbox-label">
            <input
              id="active"
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Active
          </label>
        </div>
        
        <button 
          className="save-button"
          onClick={handleSaveBasicInfo}
        >
          Save Changes
        </button>
      </div>
      
      <div className="automation-metadata">
        <div className="metadata-item">
          <span className="metadata-label">Created:</span>
          <span className="metadata-value">{formatDate(automation.created)}</span>
        </div>
        <div className="metadata-item">
          <span className="metadata-label">Last Updated:</span>
          <span className="metadata-value">{formatDate(automation.lastUpdated)}</span>
        </div>
        <div className="metadata-item">
          <span className="metadata-label">Last Triggered:</span>
          <span className="metadata-value">{formatDate(automation.lastTriggered)}</span>
        </div>
      </div>
      
      <div className="automation-conditions">
        <div className="section-header">
          <h3>Conditions</h3>
          <button 
            className="add-button"
            onClick={() => setIsAddingCondition(true)}
          >
            Add Condition
          </button>
        </div>
        
        {automation.conditions.length === 0 && !isAddingCondition ? (
          <div className="empty-message">
            No conditions defined. Add conditions to specify when this automation should trigger.
          </div>
        ) : (
          <div className="conditions-list">
            {automation.conditions.map((condition, index) => (
              <div key={index} className="condition-item">
                {editingConditionIndex === index ? (
                  <ConditionEditor 
                    condition={condition}
                    devices={devices}
                    onSave={(updatedCondition) => handleUpdateCondition(updatedCondition, index)}
                    onCancel={() => setEditingConditionIndex(null)}
                  />
                ) : (
                  <>
                    <div className="condition-summary">
                      {condition.type === 'device' && (
                        <span>
                          Device <b>{devices.find(d => d.id === condition.deviceId)?.name || condition.deviceId}</b> {' '}
                          <b>{condition.attribute}</b> {condition.operator} <b>{condition.value}</b>
                        </span>
                      )}
                      {condition.type === 'time' && (
                        <span>
                          Time {condition.operator === 'between' ? 
                            `between ${condition.start} and ${condition.end}` : 
                            `is ${condition.value}`}
                        </span>
                      )}
                      {condition.type === 'day' && (
                        <span>
                          Day {condition.operator === 'in' ? 
                            `is one of [${condition.value.join(', ')}]` : 
                            `is ${condition.value}`}
                        </span>
                      )}
                    </div>
                    <div className="condition-actions">
                      <button 
                        className="edit-button"
                        onClick={() => setEditingConditionIndex(index)}
                      >
                        Edit
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteCondition(index)}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            
            {isAddingCondition && (
              <div className="condition-item">
                <ConditionEditor 
                  devices={devices}
                  onSave={handleAddCondition}
                  onCancel={() => setIsAddingCondition(false)}
                />
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="automation-actions">
        <div className="section-header">
          <h3>Actions</h3>
          <button 
            className="add-button"
            onClick={() => setIsAddingAction(true)}
          >
            Add Action
          </button>
        </div>
        
        {automation.actions.length === 0 && !isAddingAction ? (
          <div className="empty-message">
            No actions defined. Add actions to specify what should happen when this automation triggers.
          </div>
        ) : (
          <div className="actions-list">
            {automation.actions.map((action, index) => (
              <div key={index} className="action-item">
                {editingActionIndex === index ? (
                  <ActionEditor 
                    action={action}
                    devices={devices}
                    scenes={scenes}
                    onSave={(updatedAction) => handleUpdateAction(updatedAction, index)}
                    onCancel={() => setEditingActionIndex(null)}
                  />
                ) : (
                  <>
                    <div className="action-summary">
                      {action.type === 'device' && (
                        <span>
                          Control device <b>{devices.find(d => d.id === action.device)?.name || action.device}</b>
                        </span>
                      )}
                      {action.type === 'scene' && (
                        <span>
                          Activate scene <b>{scenes.find(s => s.id === action.sceneId)?.name || action.sceneId}</b>
                        </span>
                      )}
                      {action.type === 'notification' && (
                        <span>
                          Show notification: <b>{action.message}</b>
                        </span>
                      )}
                    </div>
                    <div className="action-actions">
                      <button 
                        className="edit-button"
                        onClick={() => setEditingActionIndex(index)}
                      >
                        Edit
                      </button>
                      <button 
                        className="delete-button"
                        onClick={() => handleDeleteAction(index)}
                      >
                        Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            
            {isAddingAction && (
              <div className="action-item">
                <ActionEditor 
                  devices={devices}
                  scenes={scenes}
                  onSave={handleAddAction}
                  onCancel={() => setIsAddingAction(false)}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationEditor;